import { useEffect, useState } from 'react';
import { bakes, bakeCategories, bakeCategoryTaglines } from './bakes';
import { SiteFooter, SiteHeader } from './SiteChrome';
import './BakesPage.css';

function BakesPage() {
  const [activeCategory, setActiveCategory] = useState('All bakes');
  const [selectedBake, setSelectedBake] = useState(null);
  const visibleBakes = activeCategory === 'All bakes'
    ? bakes
    : bakes.filter((bake) => bake.category === activeCategory);

  useEffect(() => {
    if (!selectedBake) {
      return undefined;
    }

    function closeOnEscape(event) {
      if (event.key === 'Escape') {
        setSelectedBake(null);
      }
    }

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [selectedBake]);

  return (
    <div className="site-shell">
      <SiteHeader />
      <main className="bakes-page">
        <section className="bakes-page__intro section-wrap" aria-labelledby="bakes-page-title">
          <div>
            <p className="eyebrow">From our home kitchen</p>
            <h1 id="bakes-page-title">Whisking up<br /><em>your next favorite.</em></h1>
          </div>
          <div className="bakes-page__intro-copy">
            <p>Little celebrations, slow mornings, and treats for the trip home. Find a favourite, then tap a tile to get to know it.</p>
            <span>Small-batch, made by hand, made to order.</span>
          </div>
        </section>

        <section className="bake-catalogue section-wrap" aria-label="Browse all baked products">
          <div className="bake-catalogue__toolbar">
            <div className="bake-filters" role="group" aria-label="Filter bakes by category">
              {bakeCategories.map((category) => (
                <button
                  className={`bake-filter${activeCategory === category ? ' is-active' : ''}`}
                  type="button"
                  aria-pressed={activeCategory === category}
                  key={category}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <p className="bake-catalogue__count" role="status" aria-live="polite">
              {visibleBakes.length} {visibleBakes.length === 1 ? 'bake' : 'bakes'}
            </p>
          </div>

          {(activeCategory === 'All bakes' ? bakeCategories.slice(1) : [activeCategory]).map((category) => {
            const categoryBakes = visibleBakes.filter((bake) => bake.category === category);
            if (categoryBakes.length === 0) {
              return null;
            }

            const categoryId = `category-${category.toLowerCase().replace(/\s+/g, '-')}`;

            return (
              <section className="bake-category" key={category} aria-labelledby={categoryId}>
                <div className="bake-category__heading">
                  <div>
                    <p className="eyebrow">From the kitchen</p>
                    <h2 id={categoryId}>{category}</h2>
                  </div>
                  <p className="bake-category__tagline">{bakeCategoryTaglines[category]}</p>
                  <span className="bake-category__count">{categoryBakes.length} {categoryBakes.length === 1 ? 'bake' : 'bakes'}</span>
                </div>
                <div className="bake-catalogue__grid">
                  {categoryBakes.map((bake, index) => (
                    <article className="catalogue-card" key={bake.name}>
                      <button
                        className="catalogue-tile"
                        type="button"
                        aria-label={`View details for ${bake.name}`}
                        onClick={() => setSelectedBake(bake)}
                        style={{ '--tile-index': index }}
                      >
                        <span className="catalogue-tile__image">
                          <img src={bake.image} alt={bake.alt} loading="lazy" />
                          <span className="catalogue-tile__number">{String(index + 1).padStart(2, '0')}</span>
                          <span className="catalogue-tile__arrow" aria-hidden="true">↗</span>
                        </span>
                        <span className="catalogue-tile__body">
                          <span className="catalogue-tile__category">{bake.category}</span>
                          <span className="catalogue-tile__name">{bake.name}</span>
                          <span className="catalogue-tile__description">{bake.description}</span>
                          <span className="catalogue-tile__action">Meet this bake <span aria-hidden="true">↗</span></span>
                        </span>
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </section>

        <section className="bakes-page__closing" aria-labelledby="bakes-closing-title">
          <p className="eyebrow">Have something else in mind?</p>
          <h2 id="bakes-closing-title">Let's dream up <em>your bake.</em></h2>
          <a className="button button--dark" href="/#order">Tell us about it <span aria-hidden="true">↗</span></a>
        </section>
      </main>

      {selectedBake && (
        <div className="bake-dialog-backdrop" onClick={() => setSelectedBake(null)}>
          <section
            className="bake-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bake-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="bake-dialog__close" type="button" autoFocus onClick={() => setSelectedBake(null)}>
              Close <span aria-hidden="true">×</span>
            </button>
            <div className="bake-dialog__image">
              <img src={selectedBake.image} alt={selectedBake.alt} />
            </div>
            <div className="bake-dialog__copy">
              <p className="eyebrow">{selectedBake.category} · Made to order</p>
              <h2 id="bake-dialog-title">{selectedBake.name}</h2>
              <p>{selectedBake.detail}</p>
              <a className="button button--dark" href={`/?bake=${encodeURIComponent(selectedBake.name)}#order`}>
                Ask about this bake <span aria-hidden="true">↗</span>
              </a>
            </div>
          </section>
        </div>
      )}
      <SiteFooter />
    </div>
  );
}

export default BakesPage;